import { useState } from "react";
import { useMatchObjection } from "@/hooks/useObjectionLibrary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, MessageSquare, Lightbulb, Copy, Check } from "lucide-react";
import { toast } from "sonner";

interface MatchResult {
  id: string;
  category: string;
  primary_response: string;
  alternative_responses: string[];
  strategy: string | null;
  matched_keywords: string[];
  confidence: number;
}

export function ObjectionMatcher() {
  const [text, setText] = useState("");
  const [results, setResults] = useState<{
    matches: MatchResult[];
    ai_enhanced_response: string | null;
    has_match: boolean;
  } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const matchObjection = useMatchObjection();

  const handleMatch = async () => {
    if (!text.trim()) return;

    const result = await matchObjection.mutateAsync(text);
    setResults(result);
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Response copied");
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          AI Objection Handler
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter customer objection or concern..."
            rows={3}
          />
          <Button 
            onClick={handleMatch} 
            disabled={matchObjection.isPending || !text.trim()}
            className="w-full"
          >
            {matchObjection.isPending ? "Analyzing..." : "Find Responses"}
          </Button>
        </div>

        {results && (
          <div className="space-y-4">
            {results.ai_enhanced_response && (
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">AI-Enhanced Response</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleCopy(results.ai_enhanced_response!, "ai")}
                  >
                    {copiedId === "ai" ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-sm">{results.ai_enhanced_response}</p>
              </div>
            )}

            {results.matches.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Matching Templates ({results.matches.length})
                </h4>
                {results.matches.map((match) => (
                  <div 
                    key={match.id} 
                    className="p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{match.category}</Badge>
                        <Badge variant="secondary">{match.confidence}% match</Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleCopy(match.primary_response, match.id)}
                      >
                        {copiedId === match.id ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-sm mb-2">{match.primary_response}</p>
                    {match.strategy && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Lightbulb className="h-3 w-3" />
                        {match.strategy}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {match.matched_keywords.map((kw) => (
                        <Badge key={kw} variant="secondary" className="text-xs">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No matching objection handlers found</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
